/**
 * AuthenticatedImage — laed ein PropertyFile (Image) via authentifizierten API-Call
 * und zeigt es als <img>.
 *
 * Warum nicht einfach <img src="api/..." />?
 *   - <img>-Tag kann KEINE Authorization-Header mitschicken
 *   - Cookie-basierte Auth haben wir nicht (Bearer-only)
 *   - Loesung: Blob via fetch → ObjectURL → <img src={objectUrl}>
 *
 * React-Query cached den Blob pro (propertyId, fileId, variant) — beim Re-Mount der
 * Liste kein Re-Fetch. ObjectURL wird auf Unmount sauber revoked.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useApi } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

export type ImageVariant = 'thumb' | 'medium' | 'original';

interface AuthenticatedImageProps {
  propertyId: string;
  fileId: string;
  variant?: ImageVariant;
  alt: string;
  className?: string;
  onClick?: () => void;
}

export function AuthenticatedImage({
  propertyId,
  fileId,
  variant = 'thumb',
  alt,
  className,
  onClick,
}: AuthenticatedImageProps) {
  const api = useApi();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const blobQuery = useQuery({
    queryKey: ['propertyFile', 'blob', propertyId, fileId, variant],
    queryFn: () => api.propertyFiles.downloadVariant(propertyId, fileId, variant),
    staleTime: 5 * 60_000, // Backend sendet Cache-Control: 5min
  });

  useEffect(() => {
    if (!blobQuery.data) return;
    const url = URL.createObjectURL(blobQuery.data);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blobQuery.data]);

  if (blobQuery.isLoading || !objectUrl) {
    return (
      <div
        role="status"
        aria-label={`${alt} wird geladen`}
        className={cn('bg-card-alt border-line animate-pulse rounded-md border', className)}
      />
    );
  }
  if (blobQuery.isError) {
    // Nur „Fehler" beantwortet die Frage nicht, welches Bild fehlt — der Alternativtext gehört
    // auch in den Fehlerfall, sonst ist die Kachel für die Sprachausgabe leer.
    return (
      <div
        role="img"
        aria-label={`${alt} — Bild konnte nicht geladen werden`}
        title={`${alt} — Bild konnte nicht geladen werden`}
        className={cn(
          'bg-card-alt border-line text-sealwax flex items-center justify-center rounded-md border p-1 text-center text-[10px]',
          className,
        )}
      >
        Nicht geladen
      </div>
    );
  }
  return (
    <img
      src={objectUrl}
      alt={alt}
      onClick={onClick}
      className={cn('block object-cover', onClick && 'cursor-pointer', className)}
      loading="lazy"
    />
  );
}
